// ===================================
// ADMIN SALESFORCE PAGE - STAGE 7
// src/pages/AdminSalesforce.js
// Salesforce integration management
// ===================================

import React, { useState, useEffect } from 'react';
import { 
  getSalesforceStatus, 
  getSalesforceConfig, 
  testSalesforceAuth,
  getSyncStatus,
  syncAllUsers,
  syncAllSessions
} from '../services/adminApi';
import './AdminSalesforce.css';

const AdminSalesforce = () => {
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({ users: false, sessions: false });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statusRes, configRes, syncRes] = await Promise.all([
        getSalesforceStatus().catch(() => ({ connected: false })),
        getSalesforceConfig().catch(() => ({ configured: false })),
        getSyncStatus().catch(() => null)
      ]);
      setStatus(statusRes);
      setConfig(configRes);
      setSyncStatus(syncRes);
    } catch (err) {
      setError('Failed to load Salesforce status');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setError(null);
      const result = await testSalesforceAuth();
      if (result.success) {
        setSuccess('Connection successful!');
        loadData();
      } else {
        setError(result.error || 'Connection failed');
      }
      setTimeout(() => { setSuccess(null); setError(null); }, 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Connection test failed');
    }
  };

  const handleSyncUsers = async () => {
    try {
      setSyncing(prev => ({ ...prev, users: true }));
      setError(null);
      const result = await syncAllUsers(100);
      setSuccess(`Synced ${result.results?.success || 0} users`);
      loadData();
    } catch (err) {
      setError('Failed to sync users');
    } finally {
      setSyncing(prev => ({ ...prev, users: false }));
      setTimeout(() => setSuccess(null), 5000);
    }
  };

  const handleSyncSessions = async () => {
    try {
      setSyncing(prev => ({ ...prev, sessions: true }));
      setError(null);
      const result = await syncAllSessions(100);
      setSuccess(`Synced ${result.results?.success || 0} sessions`);
      loadData();
    } catch (err) {
      setError('Failed to sync sessions');
    } finally {
      setSyncing(prev => ({ ...prev, sessions: false }));
      setTimeout(() => setSuccess(null), 5000);
    }
  };

  if (loading) {
    return (
      <div className="admin-salesforce-page">
        <div className="loading-container"><div className="spinner"></div><p>Loading Salesforce status...</p></div>
      </div>
    );
  }

  return (
    <div className="admin-salesforce-page">
      <div className="page-header">
        <div className="header-content">
          <h1>☁️ Salesforce Integration</h1>
          <p>Manage Salesforce connection and data sync</p>
        </div>
        <button onClick={loadData} className="btn btn-secondary">🔄 Refresh</button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="sf-grid">
        {/* Connection Status */}
        <div className="sf-card">
          <div className="card-header">
            <h3>Connection Status</h3>
            <span className={`status-indicator ${status?.connected ? 'connected' : 'disconnected'}`}>
              {status?.connected ? '✅ Connected' : '❌ Disconnected'}
            </span>
          </div>
          <div className="card-body">
            {config && (
              <div className="config-list">
                <div className="config-item">
                  <span className="config-label">Auth URL:</span>
                  <span className={`config-value ${config.authUrl ? 'set' : 'missing'}`}>
                    {config.authUrl ? '✓ Configured' : '✗ Missing'}
                  </span>
                </div>
                <div className="config-item">
                  <span className="config-label">Base URL:</span>
                  <span className={`config-value ${config.baseUrl ? 'set' : 'missing'}`}>
                    {config.baseUrl ? '✓ Configured' : '✗ Missing'}
                  </span>
                </div>
                <div className="config-item">
                  <span className="config-label">API Version:</span>
                  <span className="config-value">{config.apiVersion || 'Not set'}</span>
                </div>
                <div className="config-item">
                  <span className="config-label">Configured:</span>
                  <span className={`config-value ${config.configured ? 'set' : 'missing'}`}>
                    {config.configured ? '✓ Yes' : '✗ No'}
                  </span>
                </div>
              </div>
            )}
            <button onClick={handleTestConnection} className="btn btn-primary btn-block">
              🔌 Test Connection
            </button>
          </div>
        </div>

        {/* Record Counts */}
        <div className="sf-card">
          <div className="card-header"><h3>Salesforce Records</h3></div>
          <div className="card-body">
            {status?.recordCounts ? (
              <div className="record-counts">
                <div className="count-item">
                  <span className="count-icon">👥</span>
                  <span className="count-value">{status.recordCounts.users || 0}</span>
                  <span className="count-label">Users</span>
                </div>
                <div className="count-item">
                  <span className="count-icon">📋</span>
                  <span className="count-value">{status.recordCounts.sessions || 0}</span>
                  <span className="count-label">Sessions</span>
                </div>
                <div className="count-item">
                  <span className="count-icon">💰</span>
                  <span className="count-value">{status.recordCounts.payments || 0}</span>
                  <span className="count-label">Payments</span>
                </div>
              </div>
            ) : (
              <p className="no-data">Connect to Salesforce to view records</p>
            )}
          </div>
        </div>

        {/* Sync Status */}
        <div className="sf-card wide">
          <div className="card-header"><h3>Sync Status</h3></div>
          <div className="card-body">
            {syncStatus ? (
              <div className="sync-comparison">
                <table className="sync-table">
                  <thead>
                    <tr>
                      <th>Object</th>
                      <th>Local DB</th>
                      <th>Salesforce</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Users</td>
                      <td>{syncStatus.users?.local || 0}</td>
                      <td>{syncStatus.users?.salesforce || 0}</td>
                      <td>
                        <span className={`sync-status ${syncStatus.users?.local === syncStatus.users?.salesforce ? 'synced' : 'unsynced'}`}>
                          {syncStatus.users?.local === syncStatus.users?.salesforce ? '✓ Synced' : '⚠ Needs Sync'}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td>Sessions</td>
                      <td>{syncStatus.sessions?.local || 0}</td>
                      <td>{syncStatus.sessions?.salesforce || 0}</td>
                      <td>
                        <span className={`sync-status ${syncStatus.sessions?.local === syncStatus.sessions?.salesforce ? 'synced' : 'unsynced'}`}>
                          {syncStatus.sessions?.local === syncStatus.sessions?.salesforce ? '✓ Synced' : '⚠ Needs Sync'}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td>Payments</td>
                      <td>{syncStatus.payments?.local || 0}</td>
                      <td>{syncStatus.payments?.salesforce || 0}</td>
                      <td>
                        <span className={`sync-status ${syncStatus.payments?.local === syncStatus.payments?.salesforce ? 'synced' : 'unsynced'}`}>
                          {syncStatus.payments?.local === syncStatus.payments?.salesforce ? '✓ Synced' : '⚠ Needs Sync'}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="no-data">Connect to Salesforce to view sync status</p>
            )}
          </div>
        </div>

        {/* Sync Actions */}
        <div className="sf-card">
          <div className="card-header"><h3>Manual Sync</h3></div>
          <div className="card-body">
            <p className="sync-description">Manually sync data between your database and Salesforce.</p>
            <div className="sync-buttons">
              <button 
                onClick={handleSyncUsers} 
                className="btn btn-secondary btn-block"
                disabled={syncing.users || !status?.connected}
              >
                {syncing.users ? '⏳ Syncing Users...' : '👥 Sync All Users'}
              </button>
              <button 
                onClick={handleSyncSessions} 
                className="btn btn-secondary btn-block"
                disabled={syncing.sessions || !status?.connected}
              >
                {syncing.sessions ? '⏳ Syncing Sessions...' : '📋 Sync All Sessions'}
              </button>
            </div>
            {!status?.connected && (
              <p className="warning-text">⚠️ Connect to Salesforce first to enable syncing</p>
            )}
          </div>
        </div>

        {/* Custom Objects */}
        <div className="sf-card">
          <div className="card-header"><h3>Custom Objects</h3></div>
          <div className="card-body">
            {config?.objects ? (
              <div className="objects-list">
                <div className="object-item">
                  <span className="object-label">User Object:</span>
                  <code>{config.objects.user}</code>
                </div>
                <div className="object-item">
                  <span className="object-label">Session Object:</span>
                  <code>{config.objects.session}</code>
                </div>
                <div className="object-item">
                  <span className="object-label">Payment Object:</span>
                  <code>{config.objects.payment}</code>
                </div>
              </div>
            ) : (
              <p className="no-data">Objects not configured</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSalesforce;
